import React, { useEffect, useReducer, useMemo } from 'react';

import useAuth from '@hooks/useAuth';
import tricorService from '@services/tricorService';
import PropTypes from 'prop-types';

import productReducer, {
  initialState,
  isCreditCard,
  isPrepaidCard,
} from './productReducer';
import { PRODUCT_TYPES } from './constants';

import _get from 'lodash/get';

ProductProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

export const ProductContext = React.createContext();

// eslint-disable-next-line max-statements
export default function ProductProvider({ children }) {
  const [{ isAuthenticated, profile }] = useAuth();
  const [state, dispatch] = useReducer(productReducer, initialState);

  const [canAccessTransfers, setCanAccessTransfers] = React.useState(true);

  const getProducts = async () => {
    const { data } = await tricorService(68, { id: _get(profile, 'id') });
    const products = await Promise.all(
      data?.products.map(async (prod) => {
        const { data } = await tricorService(16, {
          id: _get(profile, 'id'),
          entityCode: prod.entityCode,
          productCode: prod.productTypeCode,
        });
        return {
          ...prod,
          profile: data?.customer,
        };
      })
    );
    dispatch({ action: 'SET_PRODUCTS', values: products });
    dispatch({ action: 'SELECT_DEFAULT_PRODUCT' });
    dispatch({ action: 'SET_LOADING', values: false });
  };

  const getTransfersAccess = async () => {
    try {
      const { data } = await tricorService(93, {
        identificationNumber: _get(profile, 'id'),
      });
      // Verificamos si statusDescription es "Valido"
      // data.clients[0].statusDescription
      const statusDescription = _get(data, 'clients[0].statusDescription', '');
      const isValid = String(statusDescription).toLowerCase() === 'valido';

      setCanAccessTransfers(isValid);
    } catch (e) {
      setCanAccessTransfers(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      getProducts();
      getTransfersAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const creditProduct = useMemo(
    () => state.products.find(isCreditCard),
    [state.products]
  );

  const prepaidProduct = useMemo(
    () => state.products.find(isPrepaidCard),
    [state.products]
  );

  const selectedProduct = useMemo(() => {
    return state.selectedType === PRODUCT_TYPES.CREDIT
      ? creditProduct
      : state.selectedType === PRODUCT_TYPES.PREPAID
      ? prepaidProduct
      : null;
  }, [state.selectedType, creditProduct, prepaidProduct]);

  const updateProduct = (product) =>
    dispatch({ action: 'UPDATE_PRODUCT', values: product });

  const selectCreditProduct = () =>
    dispatch({ action: 'SELECT_CREDIT_PRODUCT' });

  const selectPrepaidProduct = () =>
    dispatch({ action: 'SELECT_PREPAID_PRODUCT' });

  // Validadores preventivos de estado de la cuenta Prepago (Servicio 16), por si cuenta prepago esta bloqueada, no implementa en producción
  const isPrepaidBlocked = useMemo(() => {
    if (!prepaidProduct?.profile) return false;
    const { accountStatusCode, accountStatusName } = prepaidProduct.profile;
    const blockedCodes = ['cuenta prepago bloqueada']; // no se implemento de momento el paso a cuentas que esten bloqueadas
    return (
      blockedCodes.includes(String(accountStatusCode).toUpperCase()) ||
      blockedCodes.includes(String(accountStatusName).toUpperCase())
    );
  }, [prepaidProduct]);

  return (
    <ProductContext.Provider
      value={[
        {
          ...state,
          selectedProduct,
          creditProduct,
          prepaidProduct,
          canAccessTransfers,
          isPrepaidBlocked,
        }, // [0]: state
        {
          updateProduct,
          selectCreditProduct,
          selectPrepaidProduct,
          refreshProducts: getProducts,
        }, // [1]: dispatch functions
      ]}
    >
      {children}
    </ProductContext.Provider>
  );
}
